import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Card,
  CardBody,
  Form,
  Alert,
  Row,
  Col,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  Button,
} from "reactstrap";
import SelectSearch from "react-select-search";
import InputMask from "react-input-mask";
import ReactDatetime from "react-datetime";
import moment from "moment";

import api from "../../../Api.js";
import ModalHookClassRoom from "../modal/ModalHookClassRoom.jsx";

const fallbackWeekdays = [
  { id: "0", name: "Dom" },
  { id: "1", name: "Seg" },
  { id: "2", name: "Ter" },
  { id: "3", name: "Qua" },
  { id: "4", name: "Qui" },
  { id: "5", name: "Sex" },
  { id: "6", name: "Sab" },
];

const getWeekdayOptions = () => {
  if (typeof window === "undefined") {
    return fallbackWeekdays;
  }
  const source = window?.diasSemanas;
  if (!Array.isArray(source)) {
    return fallbackWeekdays;
  }
  return source.map((item) => ({
    id: String(item?.id ?? ""),
    name: item?.name || String(item?.id ?? ""),
  }));
};

const normalizeHookArray = (hooks) => {
  if (!Array.isArray(hooks)) {
    return [];
  }
  return hooks.map((hook) => ({
    ...hook,
    url: hook?.url || "",
    type: hook?.type || "",
    params: Array.isArray(hook?.params)
      ? hook.params.map((param) => ({
          ...param,
          name: param?.name || "",
          location: param?.location || "",
          value: param?.value || "",
        }))
      : [],
  }));
};

const buildInitialForm = (mode, classroom) => {
  if (mode === "edit" && classroom) {
    return {
      id: classroom.id,
      name: classroom.name || "",
      course: classroom.course != null ? String(classroom.course) : "",
      idOnline: classroom.idOnline || "",
      openingDate: classroom.openingDate || "",
      closingDate: classroom.closingDate || "",
      dayOfTheWeek: Array.isArray(classroom.dayOfTheWeek)
        ? classroom.dayOfTheWeek.map((value) => String(value))
        : [],
      classTime: classroom.classTime || "",
      responsibleTeacher:
        classroom.responsibleTeacher != null
          ? String(classroom.responsibleTeacher)
          : "",
      publicAccess:
        classroom.publicAccess != null ? String(classroom.publicAccess) : "",
      hook: normalizeHookArray(classroom.hook),
    };
  }

  const today = moment().format("YYYY-MM-DD");
  return {
    id: null,
    name: "",
    course: "",
    idOnline: "",
    openingDate: today,
    closingDate: today,
    dayOfTheWeek: [],
    classTime: "",
    responsibleTeacher: "",
    publicAccess: "",
    hook: [],
  };
};

const normalizeSelectValue = (val) => {
  if (val && typeof val === "object" && "value" in val) {
    return String(val.value ?? "");
  }
  return val != null ? String(val) : "";
};

const ensureOptionInList = (options, value, labelFallback) => {
  try {
    if (!value) return options;
    const strValue = String(value);
    const found = Array.isArray(options)
      ? options.some((opt) => String(opt?.value ?? "") === strValue)
      : false;
    if (found) return options;
    const fallback = { value: strValue, name: labelFallback ?? strValue };
    return [fallback, ...(Array.isArray(options) ? options : [])];
  } catch (e) {
    return options;
  }
};

export default function ClassRoomCreateModal({
  isOpen,
  mode = "create",
  classroom,
  translate,
  permissions,
  companyId,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(() => buildInitialForm(mode, classroom));
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [professors, setProfessors] = useState([]);
  const [professorsLoading, setProfessorsLoading] = useState(false);
  const [hookModalOpen, setHookModalOpen] = useState(false);

  console.log("hookModalOpen", hookModalOpen);

  const hasPublicAccessPermission = useMemo(() => {
    return Array.isArray(permissions)
      ? permissions.map(String).includes("50")
      : false;
  }, [permissions]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setForm(buildInitialForm(mode, classroom));
    setFormError("");
  }, [isOpen, mode, classroom]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let active = true;
    setCoursesLoading(true);
    api
      .getAllcourses()
      .then((res) => {
        if (!active) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        setCourses(data);
      })
      .catch((error) => {
        console.error("Erro ao carregar cursos", error);
        if (active) {
          setCourses([]);
        }
      })
      .finally(() => {
        if (active) {
          setCoursesLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let active = true;

    const loadProfessors = async () => {
      try {
        if (window._professorsCache) {
          if (active) {
            setProfessors(window._professorsCache);
            setProfessorsLoading(false);
          }
          return;
        }

        if (window._professorsPromise) {
          if (active) setProfessorsLoading(true);
          const data = await window._professorsPromise;
          if (active) {
            setProfessors(data);
            setProfessorsLoading(false);
          }
          return;
        }

        if (active) setProfessorsLoading(true);
        window._professorsPromise = api
          .getUsersByAccessLevel(3)
          .then((res) => (Array.isArray(res?.data) ? res.data : []));
        const data = await window._professorsPromise;
        window._professorsCache = data;
        try {
          delete window._professorsPromise;
        } catch (e) {}
        if (active) {
          setProfessors(data);
          setProfessorsLoading(false);
        }
      } catch (error) {
        console.error("Erro ao carregar professores", error);
        if (active) {
          setProfessors([]);
          setProfessorsLoading(false);
        }
        try {
          delete window._professorsPromise;
        } catch (e) {}
      }
    };

    loadProfessors();

    return () => {
      active = false;
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
    }
  }, [onClose]);

  const handleInputChange = useCallback((field) => {
    return (event) => {
      const value = event?.target ? event.target.value : event;
      setForm((prev) => ({ ...prev, [field]: value != null ? value : "" }));
    };
  }, []);

  const toggleWeekday = useCallback((value) => {
    const strValue = String(value);
    setForm((prev) => {
      const current = Array.isArray(prev.dayOfTheWeek) ? prev.dayOfTheWeek : [];
      const exists = current.includes(strValue);
      const nextDays = exists
        ? current.filter((item) => item !== strValue)
        : [...current, strValue];
      return { ...prev, dayOfTheWeek: nextDays };
    });
  }, []);

  const handleHookChange = useCallback((nextHooks) => {
    setForm((prev) => ({ ...prev, hook: nextHooks }));
  }, []);

  const handleGenerateName = useCallback(() => {
    setForm((prev) => {
      const selectedCourse = courses.find(
        (course) => String(course.id) === String(prev.course)
      );
      const courseName = selectedCourse?.name || "";

      const weekdays = getWeekdayOptions();
      const dayLabels = (prev.dayOfTheWeek || [])
        .map((day) => {
          const match = weekdays.find((option) => option.id === String(day));
          return match?.name ? match.name.substring(0, 3).toUpperCase() : "";
        })
        .filter(Boolean);

      let composed = courseName;
      dayLabels.forEach((label, index) => {
        composed += index === 0 ? ` - ${label}` : ` ${label}`;
      });

      if (prev.classTime) {
        try {
          const time = moment(prev.classTime, "HH:mm:ss").format("HH");
          if (time && time !== "Invalid date") {
            composed += ` ${time}H`;
          }
        } catch (error) {
          console.error("Erro ao gerar hora", error);
        }
      }

      if (prev.openingDate) {
        try {
          const openingDate = moment(prev.openingDate, "YYYY-MM-DD").format(
            "DD-MM-YY"
          );
          if (openingDate && openingDate !== "Invalid date") {
            composed += ` - ${openingDate}`;
          }
        } catch (error) {
          console.error("Erro ao gerar data", error);
        }
      }

      return { ...prev, name: composed };
    });
  }, [courses]);

  const courseOptions = useMemo(() => {
    const seen = new Set();
    return courses.reduce((acc, course) => {
      const id = String(course?.id ?? "");
      if (!id || seen.has(id)) {
        return acc;
      }
      seen.add(id);
      acc.push({
        value: id,
        name: course?.name || `Curso #${id}`,
      });
      return acc;
    }, []);
  }, [courses]);

  const courseOptionsWithFallback = useMemo(() => {
    const labelFallback =
      courses.find((c) => String(c?.id) === String(form.course))?.name ||
      `Curso #${form.course}`;
    return ensureOptionInList(courseOptions, form.course, labelFallback);
  }, [courseOptions, form.course, courses]);

  const professorOptions = useMemo(() => {
    const seen = new Set();
    return professors.reduce((acc, professor) => {
      const id = String(professor?.id ?? "");
      if (!id || seen.has(id)) {
        return acc;
      }
      seen.add(id);
      const first = professor?.name || "";
      const last = professor?.lastName || "";
      const label = `${first} ${last}`.trim() || `#${id}`;
      acc.push({ value: id, name: label });
      return acc;
    }, []);
  }, [professors]);

  const professorOptionsWithFallback = useMemo(
    () =>
      ensureOptionInList(
        professorOptions,
        form.responsibleTeacher,
        professors.find(
          (p) => String(p?.id) === String(form.responsibleTeacher)
        )?.name ||
          (classroom?.dataResponsibleTeacher &&
            `${classroom.dataResponsibleTeacher.name || ""} ${
              classroom.dataResponsibleTeacher.lastName || ""
            }`.trim()) ||
          `#${form.responsibleTeacher}`
      ),
    [professorOptions, form.responsibleTeacher, professors, classroom]
  );

  const selectedProfessor = useMemo(() => {
    return form.responsibleTeacher ? String(form.responsibleTeacher) : "";
  }, [form.responsibleTeacher]);

  const selectedCourse = useMemo(() => {
    return form.course ? String(form.course) : "";
  }, [form.course]);

  const submitLabel =
    mode === "edit"
      ? translate?.["salvar_alteracoes"] || "Salvar"
      : translate?.["cadastrar"] || "Cadastrar";

  const modalTitle =
    mode === "edit"
      ? translate?.["editar_turma"] || "Editar turma"
      : translate?.["cadastrar_turma"] || "Cadastrar turma";

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (saving) {
        return;
      }
      setFormError("");
      setSaving(true);

      if (mode !== "edit") {
        const effectiveCompany = companyId || "";
        if (!effectiveCompany) {
          setFormError(
            translate?.["selecione_uma_unidade_para_continuar"] ||
              "Selecione uma unidade antes de criar a turma."
          );
          setSaving(false);
          return;
        }
      }

      if (mode === "edit" && !classroom?.id) {
        setFormError(
          translate?.["erro_ao_salvar_turma"] ||
            "Não foi possível salvar a turma."
        );
        setSaving(false);
        return;
      }

      try {
        if (mode === "edit") {
          const payload = {
            ...form,
            dayOfTheWeek: Array.isArray(form.dayOfTheWeek)
              ? form.dayOfTheWeek
              : [],
            hook: normalizeHookArray(form.hook),
          };
          delete payload.course;
          delete payload.id;

          const response = await api.putClassRoom(classroom?.id, payload);
          const updatedClassroom = response?.data?.classroom;
          if (updatedClassroom && typeof onSaved === "function") {
            await onSaved({ classroom: updatedClassroom, mode: "edit" });
          }
        } else {
          const payload = {
            ...form,
            company: companyId,
            hook: normalizeHookArray(form.hook),
            dayOfTheWeek: Array.isArray(form.dayOfTheWeek)
              ? form.dayOfTheWeek
              : [],
          };

          const response = await api.postClassRoom(payload);
          const newClassroom = response?.data?.classroom;
          if (newClassroom && typeof onSaved === "function") {
            await onSaved({ classroom: newClassroom, mode: "create" });
          }
        }
      } catch (error) {
        console.error("Erro ao salvar turma", error);
        const message =
          error?.response?.data?.msg ||
          translate?.["erro_ao_salvar_turma"] ||
          "Não foi possível salvar a turma.";
        setFormError(message);
      } finally {
        setSaving(false);
      }
    },
    [saving, mode, companyId, translate, form, classroom, onSaved]
  );

  const weekdays = useMemo(() => getWeekdayOptions(), []);

  return (
    <>
      <Modal
        className="modal-dialog-centered"
        isOpen={isOpen}
        toggle={handleClose}
      >
        <div className="modal-body p-0">
          <Card className="bg-secondary border-0 mb-0">
            <div className="modal-header">
              <h6 className="modal-title" id="modal-title-default">
                {modalTitle}
              </h6>
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={handleClose}
              >
                <span aria-hidden={true}>×</span>
              </button>
            </div>
            <CardBody className="px-lg-5">
              <Form autoComplete="off" role="form" onSubmit={handleSubmit}>
                {formError && (
                  <Alert color="danger" className="animated fadeIn">
                    <strong>{translate?.["ops"] || "Ops"}!</strong> {formError}
                  </Alert>
                )}

                <Row>
                  <Col md={12}>
                    <h5>{translate?.["nome"] || "Nome"}:</h5>
                    <FormGroup className="mb-3">
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-caps-small" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          type="text"
                          value={form.name || ""}
                          onChange={handleInputChange("name")}
                        />
                      </InputGroup>
                      <Button
                        onClick={handleGenerateName}
                        style={{ marginTop: "5px" }}
                        type="button"
                        size="sm"
                        color="secondary"
                      >
                        {translate?.["gerar_nome"] || "Gerar nome"}
                      </Button>
                    </FormGroup>
                  </Col>
                  {mode !== "edit" && (
                    <Col md={12}>
                      <h5>{translate?.["curso"] || "Curso"}</h5>
                      <FormGroup className="mb-3 select_course">
                        <InputGroup className="input-group-merge input-group-alternative select_course">
                          <SelectSearch
                            options={courseOptionsWithFallback}
                            value={selectedCourse}
                            onChange={(val) =>
                              handleInputChange("course")(
                                normalizeSelectValue(val)
                              )
                            }
                            placeholder={
                              coursesLoading
                                ? translate?.["carregando"] || "Carregando..."
                                : translate?.["escolha_um_curso"] ||
                                  "Escolha um curso"
                            }
                            search
                            disabled={coursesLoading}
                            printOptions={coursesLoading ? "never" : undefined}
                          />
                        </InputGroup>
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={6}>
                    <h5>
                      {translate?.["data_de_abertura"] || "Data de abertura"}:
                    </h5>
                    <FormGroup className="mb-3">
                      <InputGroup className="input-group-merge input-group-alternative">
                        <ReactDatetime
                          autoComplete="off"
                          locale="pt-br"
                          value={
                            form.openingDate
                              ? moment(form.openingDate, "YYYY-MM-DD")
                              : null
                          }
                          dateFormat="DD/MM/YYYY"
                          timeFormat={false}
                          onChange={(value) => {
                            if (!value) {
                              handleInputChange("openingDate")("");
                              return;
                            }
                            if (typeof value.format === "function") {
                              handleInputChange("openingDate")(
                                value.format("YYYY-MM-DD")
                              );
                            }
                          }}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <h5>
                      {translate?.["data_de_fechamento"] ||
                        "Data de fechamento"}
                      :
                    </h5>
                    <FormGroup className="mb-3">
                      <InputGroup className="input-group-merge input-group-alternative">
                        <ReactDatetime
                          autoComplete="off"
                          locale="pt-br"
                          value={
                            form.closingDate
                              ? moment(form.closingDate, "YYYY-MM-DD")
                              : null
                          }
                          dateFormat="DD/MM/YYYY"
                          timeFormat={false}
                          onChange={(value) => {
                            if (!value) {
                              handleInputChange("closingDate")("");
                              return;
                            }
                            if (typeof value.format === "function") {
                              handleInputChange("closingDate")(
                                value.format("YYYY-MM-DD")
                              );
                            }
                          }}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col md={12} style={{ marginBottom: "10px" }}>
                    <h5>{translate?.["dia_da_semana"] || "Dia da semana"}</h5>
                    <div className="row">
                      {weekdays.map((weekday) => {
                        const checkboxId = `weekday_${weekday.id}`;
                        return (
                          <div key={checkboxId} className="col-md-6">
                            <div className="custom-control custom-checkbox">
                              <input
                                className="custom-control-input dia_aula_turma"
                                id={checkboxId}
                                type="checkbox"
                                checked={form.dayOfTheWeek.includes(weekday.id)}
                                onChange={() => toggleWeekday(weekday.id)}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor={checkboxId}
                              >
                                {weekday.name}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                  <Col md={12}>
                    <h5>{translate?.["horario_da_aula"] || "Horário"}</h5>
                    <FormGroup className="mb-3">
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="far fa-clock" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <InputMask
                          value={form.classTime || ""}
                          onChange={handleInputChange("classTime")}
                          className="form-control"
                          mask="99:99:99"
                          maskChar=" "
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <h5>
                      {translate?.["professor_responsavel"] || "Professor"}
                    </h5>
                    <FormGroup className="mb-3">
                      <InputGroup className="input-group-merge input-group-alternative select_professor_bb">
                        <SelectSearch
                          options={professorOptionsWithFallback}
                          value={selectedProfessor}
                          onChange={(val) =>
                            handleInputChange("responsibleTeacher")(
                              normalizeSelectValue(val)
                            )
                          }
                          placeholder={
                            professorsLoading
                              ? translate?.["carregando"] || "Carregando..."
                              : translate?.["escolha_um_professor"] ||
                                "Escolha um professor"
                          }
                          search
                          disabled={professorsLoading}
                          printOptions={professorsLoading ? "never" : undefined}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  {hasPublicAccessPermission && (
                    <Col md={12}>
                      <h5>
                        {translate?.["permitir_acesso_publico"] ||
                          "Permitir acesso público"}
                      </h5>
                      <FormGroup className="mb-3">
                        <InputGroup className="input-group-merge input-group-alternative">
                          <Input
                            type="select"
                            value={form.publicAccess || ""}
                            onChange={handleInputChange("publicAccess")}
                          >
                            <option value=""></option>
                            <option value="1">
                              {translate?.["sim"] || "Sim"}
                            </option>
                            <option value="0">
                              {translate?.["nao"] || "Não"}
                            </option>
                          </Input>
                        </InputGroup>
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={12}>
                    <h5>
                      {translate?.["indetificador_online"] ||
                        "Identificador Online"}
                      :
                    </h5>
                    <FormGroup className="mb-3">
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-email-83" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          autoComplete="off"
                          type="text"
                          value={form.idOnline || ""}
                          onChange={handleInputChange("idOnline")}
                        />
                      </InputGroup>
                    </FormGroup>
                    <h5>
                      {translate?.[
                        "os_alunos_poderam_acessar_a_turma_pela_url"
                      ] || "Os alunos poderão acessar a turma pela URL:"}
                    </h5>
                    <h6>
                      {window.location.origin}/classenter/
                      <font style={{ color: "green" }}>{form.idOnline}</font>
                    </h6>
                  </Col>
                  {hasPublicAccessPermission && form.publicAccess == "1" && (
                    <Col md={12}>
                      <h5>
                        {translate?.["link_de_acesso_publico"] ||
                          "Link de acesso público:"}
                      </h5>
                      <h6>
                        {window.location.origin}/publicclassenter/
                        <font style={{ color: "green" }}>{form.idOnline}</font>
                      </h6>
                      <font
                        onClick={() => {
                          console.log("clicou");
                          setHookModalOpen(true);
                        }}
                        style={{
                          color: "#5e72e4",
                          position: "absolute",
                          fontSize: "10px",
                          bottom: "-8px",
                          cursor: "pointer",
                        }}
                      >
                        {translate?.["configurar_hook"] || "Configurar hook"}
                      </font>
                    </Col>
                  )}
                </Row>

                <div className="text-center">
                  <Button
                    className="my-4"
                    color="default"
                    type="button"
                    onClick={handleClose}
                    disabled={saving}
                  >
                    {translate?.["cancelar"] || "Cancelar"}
                  </Button>
                  <Button
                    className="my-4"
                    color="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving
                      ? translate?.["salvando"] || "Salvando..."
                      : submitLabel}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </div>
      </Modal>

      {hookModalOpen === true && (
        <>
          <ModalHookClassRoom
            isOpen={hookModalOpen}
            hook={form.hook}
            onChange={handleHookChange}
            onClose={() => setHookModalOpen(false)}
          />
        </>
      )}
    </>
  );
}
